import { DestroyRef, Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  delay,
  distinctUntilChanged,
  filter,
  forkJoin,
  from,
  map,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { BackendTemplate, OrganizationPhoto, ReportConfig, ReportTemplate, TemplateImage } from './interfaces';
import { String } from 'typescript-string-operations';
import { ApiBaseService } from '@core/services/api-base.service';
import { AppConstants } from '@core/constants';
import { ManageOrganizationsService } from '@core/services/manage-organizations/manage-organizations.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { ManageOrganizationDto } from '@core/services/manage-organizations/manage-organization-dto';
import { TestDto } from '@core/services/test/test-dto';
import { SessionService } from '@core/services/session.service';
import { LibraryPage } from './interfaces';
import { ImageElement, ReportElement } from './interfaces/elements';
import { ReportingApiService } from './services/reporting-api.service';
import { ReducedTheme, ReportTheme } from './interfaces/report-theme.interface';
import { Themes } from './themes';

import newPages from './mocks/data/new-page.json';
import { clampZoom } from './shared';
import { createState } from '@shared/helpers/create-state.helper';
import { ElementType } from './types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TestCache } from '@shared/services/test.cache';
import { FeatureManagementService } from '@shared/services/feature-management/feature-management.service';
import { FeatureFlagEnum } from '@shared/enums/feature-flag.enum';
import { ActiveTemplateLocalStorageKey, TrainingZoneDefaluts } from './constants';
import { ReadReportTemplates, ReportTemplateService } from '@core/services/report-template/report-template.service';
import { environment } from 'src/environments/environment';

// TODO: Used to generate new pages
const modPages: LibraryPage[] = newPages as unknown as LibraryPage[];

interface TestTemplateDetails {
  testId: number;
  hasTemplate: boolean;
  sportSlug: string;
}

@Injectable({
  providedIn: 'root',
})
export class ReportGeneratorStore {
  private apibaseService: ApiBaseService = inject(ApiBaseService);
  private organizationService: ManageOrganizationsService = inject(ManageOrganizationsService);
  private reportingApiService = inject(ReportingApiService);
  private readonly testCache = inject(TestCache);
  private snackBar = inject(MatSnackBar);
  private readonly featureManagementService = inject(FeatureManagementService);
  public sessionService: SessionService = inject(SessionService);
  public reportTemplateService = inject(ReportTemplateService);

  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _templates = new BehaviorSubject<ReportTemplate[]>([]);
  private readonly _currentTemplateId = new BehaviorSubject<number | null>(null);
  private readonly _config = new BehaviorSubject<ReportConfig | null>(null);
  private readonly _pageLibrary = new BehaviorSubject<LibraryPage[]>([]);

  private slugCounter = createState<Record<string, number>>({});
  private testTrainingZoneDetails = createState<TestTemplateDetails>({
    testId: -1,
    hasTemplate: true,
    sportSlug: '',
  });

  public loading$ = this._loading.asObservable().pipe(delay(0), distinctUntilChanged());
  public templates$ = this._templates.asObservable().pipe(delay(0), shareReplay(1));
  public currentTemplateId$ = this._currentTemplateId.asObservable().pipe(delay(0), shareReplay(1));
  public config$ = this._config.asObservable().pipe(delay(0), shareReplay(1));
  public pageLibrary$ = this._pageLibrary.asObservable().pipe(delay(0), shareReplay(1));

  constructor(private destroyRef: DestroyRef) {
    this.testTrainingZoneDetails.$.subscribe((value: TestTemplateDetails) => this.defaultTrainingZones(value));
  }

  initStore(): void {
    // Post process template pages based on test data
    this.config$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((config) => config?.template != null && config?.test != null),
        tap((config) => this.runPostProcessPages(config))
      )
      .subscribe();

    // Load page library and templates
    forkJoin({
      pageLibrary: this.reportingApiService.listPages(),
      templates: this.reportingApiService.listTemplates(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ pageLibrary, templates }) => {
          const filteredPageLibrary = this.filterPagesByFeatures(pageLibrary);
          this._pageLibrary.next(filteredPageLibrary);
          this._templates.next(this.mapPagesToTemplates(filteredPageLibrary, templates));
        },
        error: (e) => {
          console.error(e);
        },
      });
  }

  get currentTemplateId(): number | null {
    return this._currentTemplateId.value;
  }

  /**
   * CRUD
   */

  fetchOldTemplates(): void {
    this.reportTemplateService
      .getReportTemplates(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: ReadReportTemplates) => {
        const currentConfig = this._config.value;
        const updatedConfig: ReportConfig = {
          ...currentConfig,
          oldTemplates: response.results,
        };
        this._config.next(updatedConfig);
      });
  }

  getTemplate(templateId: number): void {
    this.reportingApiService.getTemplate(templateId).subscribe({
      next: (template) => {
        this._currentTemplateId.next(templateId);
        const currentConfig = this._config.value;

        const currentTheme = Themes.find((t) => t.slug === template.theme?.slug ?? 'professional');
        const currentTemplate: ReportTemplate = {
          ...template,
          pages: this.injectThemeDecorationsIntoPages(template.pages as LibraryPage[], currentTheme, template),
          // pages: this.injectThemeDecorationsIntoPages(modPages, currentTheme),
          theme: template?.theme ? this.themeOverride(template.theme) : currentTheme,
          savedTemplateImageIds: this.extractSavedTemplateImageIds(template),
        };

        const updatedConfig: ReportConfig = {
          ...currentConfig,
          template: currentTemplate,
          language: currentConfig?.language || 'en',
        };

        this._config.next(updatedConfig);
      },
      error: (e) => {
        console.error(e);
      },
    });
  }

  deleteTemplate(templateId: number): void {
    this.reportingApiService.deleteTemplate(templateId).subscribe({
      next: () => {
        this._templates.next(this._templates.value.filter((t) => t.id !== templateId));
        const firstTemplate = this._templates.value[0];
        this._currentTemplateId.next(firstTemplate.id);
        localStorage.setItem(ActiveTemplateLocalStorageKey, firstTemplate.id.toString());
        this._config.next({
          ...this._config.value,
          template: firstTemplate,
        });
        this.snackBar.open('Template deleted successfully', 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
      },
      error: (e) => {
        console.error(e);
        this.snackBar.open(`Unable to delete template: ${e}`, 'OK', AppConstants.TOAST_CONFIG.ERROR);
      },
    });
  }

  saveTemplate(template: ReportTemplate, shouldCreate = false): void {
    const newTemplateData: Partial<BackendTemplate> = {
      name: template.name,
      description: template.name,
      is_published: template.is_published,
      pages: template.pages.map((p) => p.id),
      unique_template_keys: template.pages.map((p) => p.unique_template_key),
      available_languages: template.available_languages,
      element_config_overrides: template.element_config_overrides,
      theme: {
        slug: template.theme?.slug ?? 'professional',
        primary: template.theme?.colors.primary,
        secondary: template.theme?.colors.secondary,
        overrides: template.theme?.overrides,
      },
    };
    if (shouldCreate) {
      return this.createTemplate(newTemplateData);
    } else {
      return this.updateTemplate(newTemplateData);
    }
  }

  loadTestData(testId: number): void {
    this.sessionService.getCurrentUserProfile();
    const organizationId = this.sessionService.auth.organization.id;

    forkJoin({
      test: this.fetchTest(testId),
      trainingZones: this.fetchTrainingZones(testId),
      metabolicProfile: this.fetchMetabolicProfile(testId),
      organization: this.fetchOrganization(organizationId),
      organizationPhoto: this.fetchOrganizationPhoto(organizationId),
    }).subscribe({
      next: ({ test, trainingZones, metabolicProfile, organization, organizationPhoto }) => {
        const testRecord = test?.results[0];
        if (testRecord) {
          this.testTrainingZoneDetails.set({
            testId: testRecord.id,
            hasTemplate: testRecord.training_zone_template != null,
            sportSlug: testRecord.sport?.feature_slug || '',
          });
        }

        this._config.next({
          ...this._config.value,
          test: test?.results[0],
          metabolicProfile: metabolicProfile,
          organization: organization,
          organizationPhoto: organizationPhoto,
          trainingZones: trainingZones,
        });
      },
      error: (error) => {
        console.error('Unable to load test data', error);
      },
    });
  }

  private filterPagesByFeatures(pages: LibraryPage[]): LibraryPage[] {
    const accumulationIndexEnabled = this.featureManagementService.getFeatureValue(FeatureFlagEnum.ACCUMULATION_INDEX);
    const recoveryIndexEnabled = this.featureManagementService.getFeatureValue(FeatureFlagEnum.RECOVERY_INDEX);

    return pages.filter((p) => {
      const hasAccumulationIndex = p.elements.some(
        (e) => e.type === 'metabolic-capacities-single' && e.subtype === FeatureFlagEnum.ACCUMULATION_INDEX
      );
      if (hasAccumulationIndex && !accumulationIndexEnabled) {
        return false;
      }
      const hasRecoveryIndex = p.elements.some(
        (e) => e.type === 'metabolic-capacities-single' && e.subtype === FeatureFlagEnum.RECOVERY_INDEX
      );
      if (hasRecoveryIndex && !recoveryIndexEnabled) {
        return false;
      }
      return true;
    });
  }

  /**
   * Actions
   */

  updateConfig(configChanges: Partial<ReportConfig>): void {
    const currentConfig = this._config.value;
    if (!currentConfig) {
      return;
    }

    let updatedConfig = {
      ...currentConfig,
      ...configChanges,
    };

    // Check if theme slug actually changed (not just colors)
    const oldThemeSlug = currentConfig.template?.theme?.slug;
    const newThemeSlug = configChanges.template?.theme?.slug;
    const themeSlugChanged = newThemeSlug && newThemeSlug !== oldThemeSlug;

    // If theme changed, re-inject decorations into pages
    if (themeSlugChanged && updatedConfig.template) {
      // First remove old theme decoration elements
      const pagesWithoutDecorations = updatedConfig.template.pages.map((page) => ({
        ...page,
        elements: page.elements.filter((el) => !el.slug?.startsWith('theme-decoration-')),
      }));

      // Then inject new theme decorations
      const pagesWithNewDecorations = this.injectThemeDecorationsIntoPages(
        pagesWithoutDecorations,
        updatedConfig.template.theme
      );

      updatedConfig = {
        ...updatedConfig,
        template: {
          ...updatedConfig.template,
          pages: pagesWithNewDecorations,
        },
      };
    }

    const updatedChanges = currentConfig.changes ?? [];
    updatedChanges.push({ changeType: 'theme-change' });

    this._config.next({
      ...updatedConfig,
      changes: updatedChanges,
    });
  }

  selectTemplate(templateId: number): void {
    const templates = this._templates.value;
    const selectedTemplate = templates.find((t) => t.id === templateId);

    if (!selectedTemplate) {
      console.error(`Template with ID ${templateId} not found`);
      return;
    }

    // Update current template ID
    this._currentTemplateId.next(templateId);

    // Update config with new template
    const currentConfig = this._config.value;
    const updatedConfig: ReportConfig = {
      ...currentConfig,
      template: selectedTemplate,
      language: currentConfig?.language || 'en',
      changes: [],
    };

    this._config.next(updatedConfig);
    localStorage.setItem(ActiveTemplateLocalStorageKey, templateId.toString());
  }

  insertPageAtIndex(libraryPage: LibraryPage, insertIndex: number): void {
    const currentConfig = this._config.value;
    if (!currentConfig?.template) {
      return;
    }

    // Clone the library page to create a new report page instance
    const newPageId = libraryPage.id;
    let newReportPage: LibraryPage = {
      id: libraryPage.id,
      unique_template_key: this.generateUniqueTemplateKey(),
      slug: libraryPage.slug,
      name: libraryPage.name,
      section_name: libraryPage.section_name,
      elements: this.deepCloneElements(libraryPage.elements),
      orientation: libraryPage.orientation,
      is_composite: libraryPage.is_composite,
      thumbnail: libraryPage.thumbnail,
    };

    const currentTemplateTheme = currentConfig.template.theme;
    let currentTheme = currentTemplateTheme;
    const decorations = currentTemplateTheme?.decorations;
    if (decorations == null) {
      currentTheme = Themes.find((t) => t.slug === currentConfig?.template?.theme?.slug ?? 'professional');
      currentConfig.template.theme = currentTheme;
    }
    newReportPage = this.injectThemeDecorationsIntoPages([newReportPage], currentTheme)[0];

    // Create new pages array with inserted page
    const updatedPages = [...currentConfig.template.pages];
    updatedPages.splice(insertIndex, 0, newReportPage);

    // Update template and config
    const updatedTemplate = {
      ...currentConfig.template,
      pages: updatedPages,
    };

    const updatedChanges = currentConfig.changes ?? [];
    updatedChanges.push({ changeType: 'page-add', pageId: newPageId });

    const updatedConfig = {
      ...currentConfig,
      template: updatedTemplate,
      changes: updatedChanges,
    };

    this._config.next(updatedConfig);
  }

  removePageAtIndex(pageIndex: number): void {
    const currentConfig = this._config.value;
    if (!currentConfig?.template) {
      return;
    }

    const pageToRemove = currentConfig.template.pages[pageIndex];
    const updatedPages = [...currentConfig.template.pages];
    updatedPages.splice(pageIndex, 1);

    const updatedTemplate = {
      ...currentConfig.template,
      pages: updatedPages,
    };

    const updatedChanges = currentConfig.changes ?? [];
    updatedChanges.push({ changeType: 'page-remove', pageId: pageToRemove.id });

    const updatedConfig = {
      ...currentConfig,
      template: updatedTemplate,
      changes: updatedChanges,
    };

    const renderErrors = currentConfig.renderErrors;
    if (renderErrors) {
      const { [pageToRemove.id]: _, ...updatedRenderErrors } = renderErrors;
      updatedConfig.renderErrors = updatedRenderErrors;
    }

    this._config.next(updatedConfig);
  }

  reorderPages(fromIndex: number, toIndex: number): void {
    const currentConfig = this._config.value;
    if (!currentConfig?.template) {
      return;
    }

    const updatedPages = [...currentConfig.template.pages];
    const [movedPage] = updatedPages.splice(fromIndex, 1);
    updatedPages.splice(toIndex, 0, movedPage);

    const updatedTemplate = {
      ...currentConfig.template,
      pages: updatedPages,
    };

    const updatedChanges = currentConfig.changes ?? [];
    updatedChanges.push({ changeType: 'page-reorder', pageId: movedPage.id });

    const updatedConfig = {
      ...currentConfig,
      template: updatedTemplate,
      changes: updatedChanges,
    };

    this._config.next(updatedConfig);
  }

  addRenderError(pageId: number, error: string): void {
    const currentConfig = this._config.value;
    if (!currentConfig) {
      return;
    }

    const renderErrors = currentConfig.renderErrors || {};
    const pageRenderErrors = renderErrors[pageId] || [];
    const errorIndex = pageRenderErrors.findIndex((v) => v === error);
    if (errorIndex !== -1) {
      return;
    }

    pageRenderErrors.push(error);
    const updatedConfig = {
      ...currentConfig,
      renderErrors: {
        [pageId]: pageRenderErrors,
      },
    };
    this._config.next(updatedConfig);
  }

  overrideText(pageId: number, elementSlug: string, newText?: string, newHeading?: string, custom?: any): void {
    const currentConfig = this._config.value;
    if (!currentConfig) {
      return;
    }

    const newContent: { content?: string; heading?: string; custom?: any } = {};
    if (newText) {
      newContent.content = newText;
    }
    if (newHeading) {
      newContent.heading = newHeading;
    }

    if (custom) {
      newContent.custom = custom;
    }

    const newPageOverride = { [elementSlug]: newContent };
    const updatedTemplate = { ...currentConfig.template! };
    const templateOverride = updatedTemplate?.element_config_overrides ?? {};
    if (templateOverride[pageId]) {
      templateOverride[pageId] = {
        ...templateOverride[pageId],
        ...newPageOverride,
      };
    } else {
      templateOverride[pageId] = newPageOverride;
    }

    const updatedChanges = currentConfig.changes ?? [];
    updatedChanges.push({ changeType: 'content-override', elementSlug });

    const updatedConfig = {
      ...currentConfig,
      template: updatedTemplate,
      changes: updatedChanges,
    };

    this._config.next(updatedConfig);
  }

  isGlobalTemplate(): boolean {
    const currentConfig = this._config.value;
    if (!currentConfig) {
      return true;
    }

    return Boolean(!currentConfig.template?.organization);
  }

  overrideImage(pageId: number, elementSlug: string, templateImageId: number): void {
    const currentConfig = this._config.value;
    if (!currentConfig) {
      return;
    }

    const newPageOverride = { [elementSlug]: { templateImageId } };
    const updatedTemplate = { ...currentConfig.template! };
    const templateOverride = updatedTemplate?.element_config_overrides ?? {};

    if (templateOverride[pageId]) {
      templateOverride[pageId] = {
        ...templateOverride[pageId],
        ...newPageOverride,
      };
    } else {
      templateOverride[pageId] = newPageOverride;
    }

    const updatedChanges = currentConfig.changes ?? [];
    updatedChanges.push({ changeType: 'content-override', elementSlug });

    const updatedConfig = {
      ...currentConfig,
      template: updatedTemplate,
      changes: updatedChanges,
    };

    this._config.next(updatedConfig);
  }

  clearImageOverride(pageId: number, elementSlug: string): void {
    const currentConfig = this._config.value;
    if (!currentConfig?.template) return;

    const updatedTemplate = { ...currentConfig.template };
    const templateOverride = updatedTemplate?.element_config_overrides ?? {};

    if (templateOverride[pageId]?.[elementSlug]) {
      delete templateOverride[pageId][elementSlug].templateImageId;
      delete templateOverride[pageId][elementSlug].hideImage;
    }

    const updatedChanges = currentConfig.changes ?? [];
    updatedChanges.push({ changeType: 'content-override', elementSlug });

    const updatedConfig = {
      ...currentConfig,
      template: updatedTemplate,
      changes: updatedChanges,
    };

    this._config.next(updatedConfig);
  }

  hideImageOverride(pageId: number, elementSlug: string): void {
    const currentConfig = this._config.value;
    if (!currentConfig?.template) return;

    const newPageOverride = { [elementSlug]: { hideImage: true } };
    const updatedTemplate = { ...currentConfig.template };
    const templateOverride = updatedTemplate?.element_config_overrides ?? {};

    if (templateOverride[pageId]) {
      templateOverride[pageId] = {
        ...templateOverride[pageId],
        ...newPageOverride,
      };
    } else {
      templateOverride[pageId] = newPageOverride;
    }

    const updatedChanges = currentConfig.changes ?? [];
    updatedChanges.push({ changeType: 'content-override', elementSlug });

    this._config.next({ ...currentConfig, template: updatedTemplate });
  }

  uploadTemplateImage(templateId: number, file: File): Observable<TemplateImage> {
    return this.reportingApiService.uploadTemplateImage(templateId, file).pipe(
      tap((newImage) => {
        const currentConfig = this._config.value;
        if (!currentConfig?.template) return;

        const updatedTemplate = {
          ...currentConfig.template,
          images: [...(currentConfig.template.images ?? []), newImage],
        };

        this._config.next({ ...currentConfig, template: updatedTemplate });
      })
    );
  }

  deleteTemplateImage(templateId: number, imageId: number): void {
    this.reportingApiService.deleteTemplateImage(templateId, imageId).subscribe({
      next: () => {
        const currentConfig = this._config.value;
        if (!currentConfig?.template) return;

        const updatedTemplate = {
          ...currentConfig.template,
          images: currentConfig.template.images?.filter((img) => img.id !== imageId) ?? [],
        };

        this._config.next({ ...currentConfig, template: updatedTemplate });
      },
      error: (err) => console.error('Failed to delete template image', err),
    });
  }

  setZoom(newZoom: number): void {
    this._config.next({
      ...this._config.value,
      zoom: clampZoom(newZoom),
    });
  }

  private createTemplate(newTemplateData: Partial<BackendTemplate>): void {
    this.reportingApiService.createTemplate(newTemplateData).subscribe({
      next: (template) => {
        this._currentTemplateId.next(template.id);
        const currentConfig = this._config.value;

        const currentTheme = Themes.find((t) => t.slug === template.theme?.slug ?? 'professional');
        const resolvedTemplate = this.mapPagesToTemplates(this._pageLibrary.value!, [template])[0];
        const currentTemplate: ReportTemplate = {
          ...resolvedTemplate,
          pages: this.injectThemeDecorationsIntoPages(resolvedTemplate.pages as LibraryPage[], currentTheme, template),
          theme: template?.theme ? this.themeOverride(template.theme) : currentTheme,
        };

        const updatedConfig: ReportConfig = {
          ...currentConfig,
          template: currentTemplate,
          language: currentConfig?.language || 'en',
          changes: [],
        };

        this._templates.next([...this._templates.value!, currentTemplate]);
        this._config.next(updatedConfig);
        this.snackBar.open('Template created successfully', 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
      },
      error: (e) => {
        console.error(e);
        this.snackBar.open(`Unable to create template: ${e}`, 'OK', AppConstants.TOAST_CONFIG.ERROR);
      },
    });
  }

  private updateTemplate(newTemplateData: Partial<BackendTemplate>): void {
    this.reportingApiService.updateTemplate(this._currentTemplateId.value!, newTemplateData).subscribe({
      next: () => {
        this._config.next({ ...this._config.value, changes: [] });
        this.snackBar.open('Template updated successfully', 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
      },
      error: (e) => {
        console.error(e);
        this.snackBar.open(`Unable to create template: ${e}`, 'OK', AppConstants.TOAST_CONFIG.ERROR);
      },
    });
  }

  private mapPagesToTemplates(pageLibrary: LibraryPage[], templates: BackendTemplate[]): ReportTemplate[] {
    return templates.map((template) => {
      const shouldResolve =
        template.pages != null &&
        Array.isArray(template.pages) &&
        template.pages.length > 0 &&
        typeof template.pages[0] === 'number';
      if (!shouldResolve) {
        return { ...(template as ReportTemplate) };
      }

      const resolvedPages = (template.pages as number[])
        .map((pageId) => pageLibrary.find((p) => p.id === pageId))
        .filter((page) => page !== undefined) as LibraryPage[];

      const currentTheme = Themes.find((t) => t.slug === template.theme?.slug ?? 'professional');
      const currentTemplate: ReportTemplate = {
        ...template,
        pages: this.injectThemeDecorationsIntoPages(resolvedPages, currentTheme, template),
        theme: template?.theme ? this.themeOverride(template.theme) : currentTheme,
        savedTemplateImageIds: this.extractSavedTemplateImageIds(template),
      };

      return currentTemplate;
    });
  }

  private extractSavedTemplateImageIds(template: BackendTemplate): number[] {
    const templateOverrides = template.element_config_overrides;
    if (!templateOverrides) return [];

    return Object.values(templateOverrides).flatMap((pageOverride: Record<string, Record<string, any>>) =>
      Object.values(pageOverride)
        .filter((elementOverride) => elementOverride?.templateImageId != null)
        .map((elementOverride) => elementOverride.templateImageId)
    );
  }

  private themeOverride(themeOverride: ReducedTheme): ReportTheme {
    const reportTheme = { ...Themes.find((t) => t.slug === themeOverride.slug ?? 'professional')! };
    if (themeOverride.primary) {
      reportTheme.colors = {
        ...(reportTheme.colors ?? {}),
        primary: themeOverride.primary,
      };
    }
    if (themeOverride.secondary) {
      reportTheme.colors = {
        ...(reportTheme.colors ?? {}),
        secondary: themeOverride.secondary,
      };
    }
    if (themeOverride.overrides) {
      reportTheme.overrides = {
        ...(reportTheme.overrides ?? {}),
        ...(themeOverride.overrides ?? {}),
      };
    }
    return reportTheme;
  }

  private injectThemeDecorationsIntoPages(
    pages: LibraryPage[],
    theme?: ReportTheme,
    backendTemplate?: BackendTemplate
  ): LibraryPage[] {
    const unique_template_keys = backendTemplate?.unique_template_keys ?? [];
    return pages.map((page, index) => {
      // Set unique slugs for page elements
      const newPageElements = page.elements.map((element) => ({
        ...element,
        slug: element.slug ?? this.generateElementSlug(element.type),
      }));

      // Handle unique template keys
      const unique_template_key = unique_template_keys.length > 0 ? unique_template_keys[index] : undefined;
      const newPage = unique_template_key ? { ...page, unique_template_key } : page;

      if (['Cover', 'Section'].includes(page.section_name)) {
        return newPage;
      }

      const applicableDecorations =
        page.orientation === 'portrait' ? theme?.decorations!.portrait : theme?.decorations!.landscape;

      if (!applicableDecorations || !applicableDecorations.length) {
        return newPage;
      }

      // Convert decorations to image elements with z-index
      const decorationElements: ImageElement[] = applicableDecorations.map((decoration) => ({
        slug: `theme-decoration-${decoration.slug}`,
        type: 'image',
        left: decoration.left!,
        top: decoration.top!,
        width: decoration.width!,
        height: decoration.height!,
        zIndex: decoration.zIndex ?? -1,
        data: decoration.data,
        themeColor: decoration.themeColor,
      }));

      return {
        ...newPage,
        elements: [...newPageElements, ...decorationElements],
      };
    });
  }

  private fetchTest(testId: number): Observable<Record<string, any>> {
    const test = this.testCache.getTest(testId);
    if (test) {
      // Match API response
      return of({ results: [test] });
    }

    const params: URLSearchParams = new URLSearchParams({ id: testId.toString() });
    return this.apibaseService.getJson<Record<string, any>>(`${AppConstants.API.TEST.LIST}?${params.toString()}`);
  }

  private fetchMetabolicProfile(testId: number): Observable<Array<Record<string, any>>> {
    const profile = this.testCache.getMetabolicProfile(testId);
    if (profile) {
      return of(profile);
    }

    const url = String.Format(AppConstants.API.TEST.METABOLIC, testId);
    const params: URLSearchParams = new URLSearchParams({ data: 'setcard' });
    return this.apibaseService.getJson<Array<Record<string, any>>>(`${url}?${params.toString()}`);
  }

  private fetchOrganization(organizationId: number): Observable<ManageOrganizationDto> {
    return this.organizationService.getOrganizationById(organizationId);
  }

  private fetchOrganizationPhoto(organizationId: number): Observable<OrganizationPhoto> {
    const url = String.Format(AppConstants.API.ORGANIZATIONS.GET_BY_ID, organizationId) + 'photo/';
    return this.apibaseService.getJson<OrganizationPhoto>(url).pipe(catchError(() => this.loadFallbackPhoto()));
  }

  private loadFallbackPhoto(): Observable<OrganizationPhoto> {
    return from(
      fetch('/assets/images/app-logo-header.png')
        .then((res) => res.blob())
        .then((blob) =>
          this.blobToBase64(blob).then((base64) => ({
            base64Data: base64,
            contentType: 'image/png',
            size: blob.size,
          }))
        )
    );
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = () => reject('Failed to read blob');
      reader.readAsDataURL(blob);
    });
  }

  private fetchTrainingZones(testId: number): Observable<any> {
    const url = String.Format(AppConstants.API.TEST.METABOLIC, testId);
    const params: URLSearchParams = new URLSearchParams({ data: 'training-zones' });
    return this.apibaseService
      .getJson<Array<Record<string, any>>>(`${url}?${params.toString()}`)
      .pipe(map((res) => res[5].json_data));
  }

  private defaultTrainingZones(testTemplateDetails: TestTemplateDetails): void {
    if (testTemplateDetails.hasTemplate) {
      return;
    }

    const envName = environment.name as 'Prod' | 'Test' | 'Dev' | 'Local';
    const defaultTrainingZoneId = TrainingZoneDefaluts[envName][testTemplateDetails.sportSlug];
    if (!defaultTrainingZoneId) {
      return;
    }

    if (defaultTrainingZoneId === -1) {
      return;
    }

    const testId = testTemplateDetails.testId;
    this.setTrainingZoneOnTest(defaultTrainingZoneId, testId).subscribe({
      next: (value: any) => {
        this.fetchTrainingZones(testId).subscribe((trainingZones) => {
          this._config.next({
            ...this._config.value,
            trainingZones: trainingZones,
          });
        });
      },
      error: (err) => console.error(err),
    });
  }

  private setTrainingZoneOnTest(tzTemplateId: number, testId: number): Observable<Record<string, any>> {
    const url = 'tests/training_zone_update/' + testId + '/';
    return this.apibaseService.putJson<Record<string, any>>(url, { training_zone_template_id: tzTemplateId });
  }

  private generateUniqueTemplateKey(): number {
    // Generate unique key that doesn't conflict with existing pages
    const existingIds = this._config.value?.template?.pages.map((p) => p.unique_template_key) || [];
    const maxId = Math.max(...existingIds, 0);
    return maxId + 1;
  }

  private deepCloneElements(elements: ReportElement[]): ReportElement[] {
    return JSON.parse(JSON.stringify(elements));
  }

  private runPostProcessPages(config?: ReportConfig | null): void {
    if (!config) {
      return;
    }

    // Filter out pages from sections that don't work for the current test
    const test = config.test! as Record<string, any>;
    const template = config.template!;

    // Not the best filtering logic - but good enough for now
    if (test.is_ppd) {
      template.pages = template.pages.filter((p) => !p.slug.startsWith('test_data_lactate'));
    } else if (test.is_lactate) {
      template.pages = template.pages.filter((p) => !p.slug.startsWith('test_data_ppd'));
    } else if (!test.is_ppd && !test.is_lactate) {
      template.pages = template.pages.filter((p) => !p.slug.startsWith('test_data'));
    }

    // Replace cover images once test has loaded
    template.pages = template.pages.map((p) =>
      this.replaceCoverImage(p, config?.test!, template.element_config_overrides ?? null)
    );

    // Composite pages
    // template.pages = this.expandCompositePages(template.pages);
  }

  private replaceCoverImage(
    page: LibraryPage,
    test: TestDto,
    overrides: Record<number, Record<string, any>> | null
  ): LibraryPage {
    if (page.section_name !== 'Cover') {
      return page;
    }

    page.elements.map((e) => {
      if (e.slug.startsWith('sport-image')) {
        const hasImageOverride = Object.values(overrides ?? {}).some(
          (pageOverride) => pageOverride?.[e.slug]?.templateImageId != null
        );

        if (hasImageOverride) {
          return e;
        }

        const originalImageFilePath = (e as ImageElement)?.data?.data;
        if (!originalImageFilePath) {
          return e;
        }

        const urlElements = originalImageFilePath.split('/');
        let sportSlug = test.sport.feature_slug;
        if (sportSlug.startsWith('swimming_')) {
          sportSlug = 'swimming';
        }
        const newImageFilenameElements = [sportSlug, test.athlete?.gender, page.orientation];
        if (newImageFilenameElements.length !== 3) {
          return e;
        }
        const newImageFilename = newImageFilenameElements.join('__') + '.png';
        const newImageFilePath = [...urlElements?.slice(0, -1), newImageFilename].join('/');

        (e as ImageElement).data!.data = newImageFilePath;
        return e;
      } else {
        return e;
      }
    });

    return page;
  }

  private expandCompositePages(pages: LibraryPage[]): LibraryPage[] {
    const expandedPages: LibraryPage[] = [];

    pages.forEach((page) => {
      if (!page.is_composite) {
        expandedPages.push(page);
      }
      expandedPages.push(page);
    });

    return expandedPages;
  }

  /**
   * Ensures that _no elements_ in the entire PDF report have the same slug by
   * adding sequential numbers to the element types during page processing
   */
  private generateElementSlug(elementType: ElementType): string {
    const currentTypeCount = this.slugCounter.value[elementType];
    const newCount = currentTypeCount == null ? 1 : currentTypeCount + 1;
    this.slugCounter.update((v) => ({
      ...v,
      [elementType]: newCount,
    }));
    return `${elementType}-${newCount}`;
  }
}
